import React, { useState, useEffect } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { TextArea } from "@progress/kendo-react-inputs";
import { toast } from "react-toastify";
import { getLookups, createLookup, updateLookup } from "../../api/accountsAPI";
import { MdClose } from "react-icons/md";
import EditIcon from "../../assets/common/editBoxIcon.png";
import SaveIcon from "../../assets/SaveIcon.png";
import "./BillDetailsModal.scss";

const BillDetailsModal = ({ onClose, onSelectDetail }) => {
    const [billDetails, setBillDetails] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isAddingNew, setIsAddingNew] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [newDetailText, setNewDetailText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isTextAreaFocused, setIsTextAreaFocused] = useState(false);

    useEffect(() => {
        fetchBillDetails();
    }, []);

    const fetchBillDetails = async () => {
        try {
            setLoading(true);
            const response = await getLookups("BillDetails");
            // item.active && item.value (if we are going to filter base on status)
            const activeDetails = (response?.data?.data || []).filter(
                item => item.value
            );
            setBillDetails(activeDetails);
        } catch (error) {
            console.error("Error fetching bill details:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = async () => {
        if (!newDetailText.trim()) {
            toast.error("Please enter detail text");
            return;
        }

        if (newDetailText.length > 1000) {
            toast.error("Detail text cannot exceed 1000 characters");
            return;
        }

        try {
            setIsSubmitting(true);
            await createLookup("BillDetails", {
                key: "BillDetails",
                value: newDetailText,
                active: true
            });
            toast.success("Bill detail added successfully");
            setNewDetailText("");
            setIsAddingNew(false);
            await fetchBillDetails();
        } catch (error) {
            console.error("Error adding bill detail:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = async () => {
        if (!newDetailText.trim()) {
            toast.error("Please enter detail text");
            return;
        }

        if (newDetailText.length > 1000) {
            toast.error("Detail text cannot exceed 1000 characters");
            return;
        }

        try {
            setIsSubmitting(true);
            await updateLookup("BillDetails", editingItem.lookupID, {
                key: "BillDetails",
                value: newDetailText,
                active: true
            });
            toast.success("Bill detail updated successfully");
            setNewDetailText("");
            setEditingItem(null);
            await fetchBillDetails();
        } catch (error) {
            console.error("Error updating bill detail:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleSelectDetail = (detail) => {
        onSelectDetail(detail.value);
        onClose();
    };

    const startEdit = (item) => {
        setEditingItem(item);
        setNewDetailText(item.value);
        setIsAddingNew(false);
    };

    const startAddNew = () => {
        setIsAddingNew(true);
        setEditingItem(null);
        setNewDetailText("");
    };

    const cancelEditing = () => {
        setIsAddingNew(false);
        setEditingItem(null);
        setNewDetailText("");
    };

    return (
        <Dialog
            title="Edit Bill Details"
            onClose={onClose}
            height={600}
            className="dialog-sm"
        >
            <div className="d-flex flex-column m-3 rounded" style={{background:'#F8F8F8',height:'90%'}}>
                <div className="d-flex justify-content-between align-items-center mb-3 pt-3 px-3">
                    <h6 className="mb-0 fw-bold fs-md">All Details</h6>
                    <Button
                        className="bgBtnSecondary button-small"
                        onClick={startAddNew}
                        disabled={isAddingNew || editingItem}
                    >
                        Add New Details
                    </Button>
                </div>

                <div className="flex-grow-1 overflow-auto mb-3">
                    {loading ? (
                        <div className="text-center p-4">Loading...</div>
                    ) : billDetails.length === 0 ? (
                        <div className="text-center p-4 text-muted">
                            No bill details found. Add your first detail!
                        </div>
                    ) : (
                        billDetails.map((item,index) => (
                            <div
                                key={item.lookupID}
                                className="bill-detail-item d-flex align-items-start justify-content-between p-3"
                                style={{
                                    background: index % 2 === 0 ? '#F9F9FF' : '#FFFFFF',
                                }}
                            >
                                <div className="flex-grow-1 me-3" style={{ fontSize: "14px" }}>
                                    {item.value}
                                </div>
                                <div className="d-flex gap-2 align-items-center">
                                    <Button
                                        className="common-btn button-small"
                                        style={{ minWidth: "70px" }}
                                        onClick={() => handleSelectDetail(item)}
                                    >
                                        Select
                                    </Button>
                                    <img onClick={() => startEdit(item)}
                                        disabled={isAddingNew || editingItem} src={EditIcon} alt="Edit" 
                                    style={{ width: "18px", height: "18px", cursor: "pointer" }}
                                    />
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {(isAddingNew || editingItem) && (
                    <div className="border p-3 d-flex justify-content-between align-items-center bg-white">
                        <TextArea
                            value={newDetailText}
                            onChange={(e) => setNewDetailText(e.target.value)}
                            rows={5}
                            maxLength={1000}
                            style={{
                                width: "100%",
                                maxWidth: "790px",
                                height: "80px",
                                borderRadius: "5px",
                                border: isTextAreaFocused ? "1px solid #676767a8" : "1px solid #d1d1d1",
                                padding: "5px",
                                fontSize: "14px",
                                resize: "vertical",
                                transition: "border 0.2s ease",
                                outline: "none",
                            }}
                            onFocus={() => setIsTextAreaFocused(true)}
                            onBlur={() => setIsTextAreaFocused(false)}
                            placeholder="Enter bill detail text..."
                        />
                        <div

                        >
                            <div className="d-flex gap-1 align-items-center mb-3">
                                <Button
                                    className="p-1"
                                    onClick={editingItem ? handleEdit : handleAddNew}
                                    disabled={isSubmitting || !newDetailText.trim()}
                                    style={{
                                        background: "transparent",
                                        border: "none",
                                        cursor: "pointer",
                                    }}
                                    title="Save"
                                >
                                    <img src={SaveIcon} alt="Save" style={{ width: "20px", height: "20px" }} />
                                </Button>
                                <Button
                                    className="d-flex justify-content-center align-items-center bgBtnSecondary rounded-5"
                                    style={{ width: "20px", height: "20px" }}
                                    onClick={cancelEditing}
                                    disabled={isSubmitting}
                                    title="Close"
                                >
                                    <MdClose style={{ marginBottom: '.1rem' }} color="#e74c3c" />
                                </Button>
                            </div>
                            <span className="text-muted" style={{ fontSize: "12px" }}>
                                {newDetailText.length}/1000
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <DialogActionsBar>
                <div className="dialog-footer w-100">
                    <Button
                        className="common-btn bgBtnSecondary me-2"
                        onClick={onClose}
                    >
                        Close
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default BillDetailsModal;

